import {
  Component,
  computed,
  EventEmitter,
  Output,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { NgForOf, NgIf, CommonModule } from '@angular/common';
import { NodeClass } from '@modules/campaigns/Domain/NodeClass';
import { OfferPartialPaymentNode } from '@modules/campaigns/Domain/Nodes/OfferPartialPayment';
import { OfferPartialPaymentConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { NodeValidationError } from '@modules/campaigns/Domain/Interfaces/NodeValidationError';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { INodeInspectorAdapter } from '@shared/components/joint-js/services/INodeInspectorAdapter';
import { InspectorMode } from '@shared/components/joint-js/components/inspectors/node-inspector/node-inspector.component';
import { DividerModule } from 'primeng/divider';
import { PipeModule } from '@shared/pipes/pipe.module';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LucideIconsModule } from '@shared/lucide-icons.module';
import { FormsModule } from '@angular/forms';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { InputNumberModule } from 'primeng/inputnumber';
import { MessagesModule } from 'primeng/messages';

@Component({
  selector: 'app-offer-partial-payment-inspector',
  standalone: true,
  imports: [
    CommonModule,
    NgIf,
    NgForOf,
    DividerModule,
    PipeModule,
    TranslateModule,
    LucideIconsModule,
    FormsModule,
    InputNumberModule,
    MessagesModule,
  ],
  templateUrl: './offer-partial-payment-inspector.component.html',
  styleUrls: ['./offer-partial-payment-inspector.component.scss'],
})
export class OfferPartialPaymentInspectorComponent implements INodeInspectorAdapter {
  @Output() closeSidebar = new EventEmitter<void>();

  node: WritableSignal<NodeClass | null> = signal<NodeClass | null>(null);

  activeTab = signal<'preview' | 'config'>('preview');
  mode = signal<InspectorMode>('edit');

  expirationDays = signal<number | null>(1);
  minimumAmount = signal<number | null>(null);
  currency = signal<string | null>('EUR');
  minimumPercentageAmount = signal<number | null>(1);

  errors = signal<NodeValidationError[]>([]);

  private changeTimeout?: ReturnType<typeof setTimeout>;

  readonly tabs = computed(() => [
    {
      label: this.translate.instant('DATA.MODULES.INSPECTORS.OFFER_PAYMENT_AGREEMENT.TABS.PREVIEW'),
      value: 'preview' as const,
    },
    {
      label: this.translate.instant('DATA.MODULES.INSPECTORS.OFFER_PAYMENT_AGREEMENT.TABS.CONFIG'),
      value: 'config' as const,
    },
  ]);

  readonly visibleTabs = computed(() =>
    this.mode() === 'edit' ? this.tabs() : this.tabs().filter((t) => t.value === 'preview'),
  );

  constructor(
    private workflow: WorkflowStateService,
    private translate: TranslateService,
  ) {}

  get nodeValue(): OfferPartialPaymentNode | null {
    const current = this.node();
    if (!current) return null;
    return current.type === NodeType.OfferPartialPayment
      ? (current as OfferPartialPaymentNode)
      : null;
  }

  setNode(nodeSignal: Signal<NodeClass | null>) {
    this.node.set(nodeSignal());
    this.refreshFromConfig();
  }

  setMode(mode: InspectorMode) {
    this.mode.set(mode);
    this.activeTab.set(mode === 'edit' ? 'config' : 'preview');
  }

  setActiveTab(tab: 'preview' | 'config') {
    this.activeTab.set(tab);
  }

  applyChanges() {
    if (this.mode() === 'preview') return;

    const node = this.nodeValue;
    if (!node) return;

    const amount = this.minimumAmount();
    const updatedConfig: OfferPartialPaymentConfig = {
      ...(node.config as OfferPartialPaymentConfig),
      expirationDays: this.expirationDays(),
      minimumAmount: {
        amount: amount != null ? Math.round(amount * 100) : null,
        currency: this.currency(),
      },
      minimumPercentageAmount: this.minimumPercentageAmount(),
    };

    const updated = node.cloneWithConfig(updatedConfig) as OfferPartialPaymentNode;
    const validation = updated.validate();
    this.errors.set(validation);

    if (!validation.length) {
      this.workflow.addOrUpdateNode(updated);
    }
  }

  onExpirationDaysChange(value: number | null) {
    if (value == null || isNaN(value as any)) value = 0;
    if (value < 0) value = 0;
    if (value > 90) value = 90;

    this.expirationDays.set(value);
    this.scheduleApplyChanges();
  }

  onMinimumAmountChange(value: number | null) {
    this.minimumAmount.set(value);
    this.scheduleApplyChanges();
  }

  onMinimumPercentageAmountChange(value: number | null) {
    this.minimumPercentageAmount.set(value ?? null);
    this.scheduleApplyChanges();
  }

  private scheduleApplyChanges() {
    if (this.changeTimeout) clearTimeout(this.changeTimeout);
    this.changeTimeout = setTimeout(() => this.applyChanges(), 500);
  }

  private refreshFromConfig() {
    const node = this.nodeValue;
    if (!node) return;

    const cfg = (node.config ?? {}) as OfferPartialPaymentConfig;

    this.expirationDays.set(cfg.expirationDays ?? 1);
    this.minimumAmount.set(
      cfg.minimumAmount?.amount ? Number(cfg.minimumAmount.amount) / 100 : null,
    );
    this.currency.set(cfg.minimumAmount?.currency ?? 'EUR');
    this.minimumPercentageAmount.set(cfg.minimumPercentageAmount ?? null);

    this.errors.set([]);
  }
}
